import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import PageIntroCard from "@/components/PageIntroCard";
import {
  Sparkles, Timer, BookOpen, ArrowRight, Flame,
} from "lucide-react";

/**
 * Study Hub — a single landing page that replaces the separate Smart Review,
 * Mock Exam, and Abstract Exam entries in the sidebar. Renders four hero
 * cards, each a full-height CTA into the underlying flow. The existing
 * routes (/student/smart-review, /student/mock-exam, /student/abstract-exam)
 * are still active — this page just gives students one place to choose.
 */
export default function StudyHub() {
  const nav = useNavigate();
  const [dueToday, setDueToday] = useState(null);
  const [lastMock, setLastMock] = useState(null);

  useEffect(() => {
    api.get("/student/smart-review/summary")
      .then((r) => setDueToday(r.data?.due_today ?? 0))
      .catch(() => {});
    api.get("/student/mock-exam/history")
      .then((r) => {
        const rows = r.data?.sessions || r.data || [];
        setLastMock(Array.isArray(rows) && rows.length ? rows[0] : null);
      })
      .catch(() => {});
  }, []);

  const cards = [
    {
      key: "smart-review",
      title: "Smart Review",
      desc: "Personalized flashcards powered by FSRS spaced repetition — the fastest way to lock in what you'll forget.",
      icon: Sparkles,
      accent: "from-amber-500 via-orange-500 to-rose-500",
      to: "/student/smart-review",
      cta: dueToday && dueToday > 0 ? `Review ${dueToday} due card${dueToday === 1 ? "" : "s"}` : "Start a session",
      badge: dueToday && dueToday > 0 ? `${dueToday > 99 ? "99+" : dueToday} due today` : null,
      badgeTone: "bg-rose-500 text-white",
      testId: "study-hub-smart-review",
    },
    {
      key: "practice",
      title: "Practice by Course",
      desc: "Drill specific subjects & modules at your own pace. Ideal for targeting weak topics before exam day.",
      icon: BookOpen,
      accent: "from-indigo-500 via-blue-500 to-cyan-500",
      to: "/student/courses",
      cta: "Browse my courses",
      badge: null,
      testId: "study-hub-practice",
    },
    {
      key: "mock-exam",
      title: "Mock Exam",
      desc: "Timed 50/75/100-question exams that mirror board conditions. Get scored, breakdown by subject, ranked.",
      icon: Timer,
      accent: "from-emerald-500 via-teal-500 to-cyan-600",
      to: "/student/mock-exam",
      cta: "Take a mock exam",
      badge: lastMock?.score_percentage != null ? `Last: ${Math.round(lastMock.score_percentage)}%` : null,
      badgeTone: "bg-emerald-100 text-emerald-800 border-emerald-200",
      testId: "study-hub-mock-exam",
    },
  ];

  return (
    <div className="space-y-8" data-testid="study-hub-page">
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-600 dark:text-indigo-400 flex items-center gap-2">
          <Flame className="w-3.5 h-3.5" /> Study Hub
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">
          Ready to review?
        </h1>
        <p className="text-slate-600 dark:text-slate-300 mt-1 max-w-2xl">
          Pick the tool that fits how you want to study today. Everything here shares your progress
          and adapts to your exam date.
        </p>
      </div>

      <PageIntroCard
        pageKey="study-hub"
        icon={Flame}
        tone="amber"
        title="Welcome to your new Study Hub"
        body="We combined Smart Review, Practice, Mock Exam, and Abstract Reasoning into one page. Your streak, exam date, and daily cap sync across all four."
      />

      <div className="grid gap-5 sm:grid-cols-2">
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <Card
              key={c.key}
              className="relative overflow-hidden group border-slate-200 dark:border-slate-800 hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => nav(c.to)}
              data-testid={c.testId}
            >
              <div className={`absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r ${c.accent}`} />
              <div className="p-6 pt-7 flex flex-col h-full min-h-[220px]">
                <div className="flex items-start justify-between gap-3">
                  <div className={`h-12 w-12 rounded-2xl bg-gradient-to-br ${c.accent} text-white grid place-items-center shadow-md`}>
                    <Icon className="w-6 h-6" strokeWidth={2} />
                  </div>
                  {c.badge && (
                    <Badge className={c.badgeTone + " font-bold shrink-0"} data-testid={`${c.testId}-badge`}>
                      {c.badge}
                    </Badge>
                  )}
                </div>
                <h2 className="font-display font-bold text-xl mt-4 leading-tight">{c.title}</h2>
                <p className="text-sm text-slate-600 dark:text-slate-300 mt-2 leading-relaxed flex-1">
                  {c.desc}
                </p>
                <Button
                  className={`mt-5 w-full h-11 bg-gradient-to-r ${c.accent} text-white hover:opacity-95 active:scale-[0.98] transition-transform font-semibold shadow-sm`}
                  onClick={(e) => { e.stopPropagation(); nav(c.to); }}
                  data-testid={`${c.testId}-cta`}
                >
                  {c.cta} <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
